import React, { useState, useMemo } from 'react';
import './DashboardBusinessUsers.css';
import { API_BASE } from '../utils/apiBase';

function DashboardBusinessUsers({ businessUsers = [], onRefresh, token }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const MAX_BUSINESS_USERS = 10;

  // Garantir que businessUsers seja sempre um array
  const safeBusinessUsers = Array.isArray(businessUsers) ? businessUsers : [];

  // Filtrar sub-usuários
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return safeBusinessUsers;
    
    const searchLower = searchTerm.toLowerCase();
    return safeBusinessUsers.filter(user => 
      (user.email || '').toLowerCase().includes(searchLower) ||
      (user.name || '').toLowerCase().includes(searchLower) ||
      (user.company || '').toLowerCase().includes(searchLower)
    );
  }, [safeBusinessUsers, searchTerm]);

  // Criar novo sub-usuário
  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      setMessage({ type: 'error', text: 'Preencha todos os campos obrigatórios!' });
      return;
    }

    if (safeBusinessUsers.length >= MAX_BUSINESS_USERS) {
      setMessage({ type: 'error', text: `Limite de ${MAX_BUSINESS_USERS} sub-usuários atingido!` });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/business/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Falha ao criar sub-usuário');
      }

      setMessage({ type: 'success', text: 'Sub-usuário criado com sucesso!' });
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', password: '', phone: '', company: '' });
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Deletar sub-usuário
  const confirmDelete = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/business/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Falha ao excluir sub-usuário');
      }

      setMessage({ type: 'success', text: 'Sub-usuário excluído com sucesso!' });
      setShowDeleteModal(false);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Resetar senha
  const handleResetPassword = async (user) => {
    const newPassword = prompt(`Digite a nova senha para ${user.email}:`);
    if (!newPassword) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/business/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (!response.ok) {
        throw new Error('Falha ao resetar senha');
      }

      setMessage({ type: 'success', text: 'Senha atualizada com sucesso!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="business-users-container">
      {/* Header */}
      <div className="business-header">
        <div className="business-title-section">
          <h2>💼 Meus Sub-Usuários</h2>
          <p>Gerencie até {MAX_BUSINESS_USERS} sub-usuários da sua conta Business</p>
        </div>
        <div className="business-actions">
          <button className="btn-refresh" onClick={onRefresh}>
            <span className="refresh-icon">🔄</span>
            Atualizar
          </button>
          <button 
            className="btn-create" 
            onClick={() => setShowCreateModal(true)}
            disabled={safeBusinessUsers.length >= MAX_BUSINESS_USERS}
          >
            ➕ Novo Sub-Usuário
          </button>
        </div>
      </div>

      {/* Mensagem de sucesso/erro */}
      {message && (
        <div className={`business-message ${message.type}`}>
          {message.type === 'success' ? '✅' : '⚠️'} {message.text}
        </div>
      )}

      {/* Limite de sub-usuários */}
      <div className="business-limit">
        <div className="limit-progress">
          <div 
            className="limit-bar" 
            style={{ width: `${(safeBusinessUsers.length / MAX_BUSINESS_USERS) * 100}%` }}
          ></div>
        </div>
        <div className="limit-text">
          <strong>{safeBusinessUsers.length}</strong> de <strong>{MAX_BUSINESS_USERS}</strong> sub-usuários criados
        </div>
      </div>

      {/* Busca */}
      {safeBusinessUsers.length > 0 && (
        <div className="business-search">
          <input
            type="text"
            placeholder="🔍 Buscar por nome, email ou empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Lista de Sub-Usuários */}
      {safeBusinessUsers.length === 0 ? (
        <div className="business-empty">
          <div className="empty-icon">👥</div>
          <h3>Nenhum sub-usuário criado</h3>
          <p>Crie sub-usuários para compartilhar funcionalidades da sua conta Business.</p>
          <button className="btn-create-large" onClick={() => setShowCreateModal(true)}>
            ➕ Criar Primeiro Sub-Usuário
          </button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="business-empty">
          <div className="empty-icon">🔍</div>
          <h3>Nenhum resultado encontrado</h3>
          <p>Tente ajustar sua busca.</p>
        </div>
      ) : (
        <div className="business-grid">
          {filteredUsers.map(user => (
            <div key={user.id} className="business-card">
              <div className="card-header">
                <div className="user-avatar">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <h4>{user.name || 'Sem nome'}</h4>
                  <p className="user-email">✉️ {user.email}</p>
                </div>
              </div>
              
              <div className="card-details">
                {user.phone && (
                  <div className="detail-item">
                    <span className="detail-icon">📱</span>
                    <span>{user.phone}</span>
                  </div>
                )}
                {user.company && (
                  <div className="detail-item">
                    <span className="detail-icon">🏢</span>
                    <span>{user.company}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-icon">📅</span>
                  <span>Criado em {formatDate(user.createdAt)}</span>
                </div>
              </div>

              <div className="card-actions">
                <button 
                  className="btn-card-action btn-reset" 
                  onClick={() => handleResetPassword(user)}
                  title="Resetar Senha"
                >
                  🔑 Resetar Senha
                </button>
                <button 
                  className="btn-card-action btn-delete" 
                  onClick={() => handleDeleteClick(user)}
                  title="Excluir"
                >
                  🗑️ Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>➕ Criar Novo Sub-Usuário</h3>
            <div className="modal-form">
              <div className="form-group">
                <label>Nome <span className="required">*</span></label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>
              <div className="form-group">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label>Senha <span className="required">*</span></label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="form-group">
                <label>Empresa</label>
                <input
                  type="text"
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  placeholder="Nome da empresa"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setShowCreateModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal-confirm" 
                onClick={handleCreateUser}
                disabled={isLoading}
              >
                {isLoading ? <span className="btn-spinner"></span> : 'Criar Sub-Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir este sub-usuário?</p>
            {selectedUser && (
              <div className="modal-user-info">
                <strong>Nome:</strong> {selectedUser.name || 'Sem nome'}
                <br />
                <strong>Email:</strong> {selectedUser.email}
                <br />
                <strong>Criado em:</strong> {formatDate(selectedUser.createdAt)}
              </div>
            )}
            <p className="warning-text">⚠️ Esta ação não pode ser desfeita!</p>
            <div className="modal-actions">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal-confirm btn-danger" 
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? <span className="btn-spinner"></span> : 'Sim, excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardBusinessUsers;
